import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dashboardPreview from '../assets/dashboard-preview.png';
import { usePageMeta } from '../hooks/usePageMeta';

const SOURCES = [
  { name: 'RASFF',   origin: 'EU', description: 'Real-time food & feed safety notifications from the EU Rapid Alert System' },
  { name: 'EUR-Lex', origin: 'EU', description: 'EU food safety legislation' },
  { name: 'UK FSA',  origin: 'UK', description: 'Product alerts and food safety notices from the Food Standards Agency' },
  { name: 'openFDA', origin: 'US', description: 'FDA food recalls and enforcement reports' },
  { name: 'EFSA',    origin: 'EU', description: 'Scientific opinions and risk assessments from the European Food Safety Authority' },
  { name: 'CFIA',    origin: 'CA', description: 'Recalls and safety advisories from the Canadian Food Inspection Agency' },
];

const BENEFITS = [
  'Daily RASFF alerts filtered by the product categories you work with',
  'A weekly digest covering legislation, recalls and scientific updates',
  'A live dashboard of RASFF trends, hazards and upcoming legislation deadlines',
];

export default function SubscribeLandingPage() {
  usePageMeta({ title: 'Subscribe', description: 'Official alerts, recalls and regulatory updates from RASFF, EUR-Lex, UK FSA, FDA, EFSA and CFIA, curated into one dashboard and delivered to your inbox.' });
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const startSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/subscribe/form${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`);
  };

  return (
    <div className="about-page">
      {/* ── Hero — full-bleed, product-first ─────────────── */}
      <section className="about-hero">
        <div className="about-hero__copy">
          <h1 className="about-hero__title">Know what changed in food safety — before it reaches your desk</h1>
          <p className="about-hero__body">
            Official alerts, recalls and regulatory updates from RASFF, EUR-Lex, UK FSA, FDA,
            EFSA and CFIA — curated into one dashboard and delivered to your inbox.
          </p>
          <form className="about-hero__form" onSubmit={startSubscribe}>
            <input
              type="email"
              className="about-hero__email"
              placeholder="you@company.com"
              aria-label="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <button type="submit" className="about-hero__cta">Subscribe free</button>
          </form>
          <p className="about-hero__note">No account required. Unsubscribe any time.</p>
        </div>
        <Link to="/" className="about-hero__shot" aria-label="Open the live dashboard">
          <img src={dashboardPreview} alt="The Food Safety Digest RASFF dashboard" loading="lazy" />
        </Link>
      </section>

      {/* ── What you get ─────────────────────────────────── */}
      <section className="about-section">
        <h2 className="about-section__heading">What you get</h2>
        <ul className="about-benefits">
          {BENEFITS.map(b => <li key={b} className="about-benefit">{b}</li>)}
        </ul>
      </section>

      {/* ── Data we monitor ──────────────────────────────── */}
      <section className="about-section">
        <h2 className="about-section__heading">Data we monitor</h2>
        <div className="about-sources">
          {SOURCES.map(s => (
            <div key={s.name} className="about-source-row">
              <span className="about-source-row__name">{s.name}</span>
              <span className="about-source-row__origin">{s.origin}</span>
              <span className="about-source-row__desc">{s.description}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="about-cta-section">
        <h2 className="about-cta-section__heading">Ready to stay informed?</h2>
        <p className="about-cta-section__body">
          Free newsletters for food safety professionals.
        </p>
        <Link to="/subscribe/form" className="about-hero__cta">
          Subscribe for free newsletters
        </Link>
        <p className="about-cta-section__attr">
          Data sourced from RASFF, EUR-Lex, EFSA, FSA, FDA, CFIA
        </p>
      </section>
    </div>
  );
}
